import { useCallback, useMemo, useState, useEffect } from 'react';
import { View, StyleSheet, useWindowDimensions, Image, Animated } from 'react-native';
import { useNavigate } from 'react-router';
import { theme, Text, Button, Checkbox, FormInput } from '@socrait/react';
import { CredentialResponse, GoogleLogin, useGoogleOAuth } from '@react-oauth/google';

import { HyperLink } from '~/components/HyperLink';
import { Loading } from '~/components/Loading';
import { OrDivider } from '~/components/OrDivider';
import { Turnstile } from '~/components/Turnstile';
import { useLogInMutation } from '~/services/useLoginMutation';
import { useEmailPasswordSignupMutation } from '~/services/useEmailPasswordSignupMutation';
import { showError } from '~/utils/showError';
import { isPydanticValidationError, parsePydanticErrors } from '~/utils/pydanticErrors';
import { useEnvironmentVariables } from '~/hooks/useEnvironmentVariables';
import { handleTermsOfServicePress } from '~/constants';
import { PublicPageLayout } from '~/layouts/PublicPageLayout';
import { SplashPanel } from '~/components/SplashPanel';
import { PhaseAnimationProvider, usePhaseAnimation } from '~/providers/PhaseAnimationProvider';
import { AuthenticationError, ClientError, ServerError } from '~/errors';

import phase1GraphicUrl from '~/assets/images/phase1_graphic.png';
import phase2GraphicUrl from '~/assets/images/phase2_graphic.png';
import phase3GraphicUrl from '~/assets/images/phase3_graphic.png';

const LARGE_SCREEN_BREAKPOINT = 1024;

const GRAPHICS = [phase1GraphicUrl, phase2GraphicUrl, phase3GraphicUrl] as const;

const GRAPHIC_ALT_TEXTS = [
  'Just say it — Socrait organizes it',
  'No more typing, no more memory gaps',
  'Free your mind to teach',
] as const;

function AnimatedGraphicPanel() {
  const { phase, fadeAnim } = usePhaseAnimation();
  return (
    <View style={styles.rightColumn}>
      <Animated.View style={[styles.rightContent, { opacity: fadeAnim }]}>
        <Image
          source={{ uri: GRAPHICS[phase] }}
          style={styles.graphicImage}
          resizeMode="cover"
          accessibilityLabel={GRAPHIC_ALT_TEXTS[phase]}
        />
      </Animated.View>
    </View>
  );
}

export function SignupPage() {
  const [scriptLoading, setScriptLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const [isGoogleLoginPending, setIsGoogleLoginPending] = useState(false);
  const [isSignupPending, setIsSignupPending] = useState(false);
  const [view, setView] = useState<'landing' | 'email'>('landing');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tosAgreedAt, setTosAgreedAt] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const { mutate: googleLogin } = useLogInMutation();
  const { mutate: signUp } = useEmailPasswordSignupMutation();
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const navigate = useNavigate();
  const { isMockMode, turnstileSiteKey } = useEnvironmentVariables();
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= LARGE_SCREEN_BREAKPOINT;

  const hasContent =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    password.trim().length > 0 &&
    confirmPassword.trim().length > 0;

  useEffect(() => {
    setScriptLoading(true);

    const timeoutId = setTimeout(() => {
      if (!scriptLoadedSuccessfully) {
        setScriptError(true);
        setScriptLoading(false);
      }
    }, 10000);

    if (scriptLoadedSuccessfully) {
      setScriptLoading(false);
      setScriptError(false);
      clearTimeout(timeoutId);
    }

    return () => clearTimeout(timeoutId);
  }, [scriptLoadedSuccessfully]);

  const handleTosToggle = useCallback(() => {
    if (!tosAgreedAt) {
      setTosAgreedAt(new Date().toISOString());
    } else {
      setTosAgreedAt('');
    }
  }, [tosAgreedAt]);

  const tosCheckboxLabel = useMemo(
    () => (
      <View style={styles.checkboxLabelContainer}>
        <Text style={styles.checkboxLabelText}>I agree to the </Text>
        <HyperLink onPress={handleTermsOfServicePress} style={styles.checkboxLabelText}>
          Terms of Service<Text style={[styles.checkboxLabelText, { color: theme.colors.red }]}>*</Text>
        </HyperLink>
      </View>
    ),
    [],
  );

  const handleGoogleSignup = useCallback(
    (credentialResponse: CredentialResponse) => {
      setIsGoogleLoginPending(true);
      googleLogin(
        { idToken: credentialResponse.credential! },
        {
          onSuccess: () => navigate('/', { replace: true }),
          onError: (error) => {
            setIsGoogleLoginPending(false);
            showError(error, 'Sign up failed. Please try again, or contact support if the issue persists.');
          },
        },
      );
    },
    [googleLogin, navigate],
  );

  const handleGoogleSignupError = useCallback(() => {
    setIsGoogleLoginPending(false);
    showError(new Error(), 'Sign up failed. Please try again, or contact support if the issue persists.');
  }, []);

  const handleShowEmailForm = useCallback(() => {
    setView('email');
  }, []);

  const handleGoBack = useCallback(() => {
    setNameError('');
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setView('landing');
  }, []);

  const handleEmailPasswordSignup = useCallback(() => {
    const emailValid = email.trim().length > 0 && email.includes('@');
    if (!emailValid) {
      showError(new Error(), 'Please enter a valid email address.');
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      return;
    }

    if (!tosAgreedAt) {
      showError(new Error(), 'Please agree to the Terms of Service.');
      return;
    }

    if (!turnstileToken) {
      showError(new Error(), 'Please complete the security verification.');
      return;
    }

    setIsSignupPending(true);
    signUp(
      { name: name.trim(), email: email.trim(), password, tosAgreedAt, turnstileToken },
      {
        onSuccess: () => navigate('/verify', { state: { email: email.trim() } }),
        onError: (error) => {
          setIsSignupPending(false);
          setTurnstileToken(null);
          setTurnstileKey((k) => k + 1);

          if (error instanceof AuthenticationError && error.statusCode === 403) {
            if (error.errorCode === 'password_pwned') {
              showError(error, 'This password has appeared in a data breach, please choose a different one.');
            } else if (error.errorCode === 'email_password_auth_disabled') {
              showError(error, 'Email signup is currently unavailable, please use Google signup instead.');
            } else {
              showError(error, 'Verification failed, please try again.');
            }
          } else if (error instanceof ClientError && error.statusCode === 429) {
            const seconds = error.retryAfter ?? 60;
            showError(error, `Too many attempts, please try again in ${seconds} seconds.`);
          } else if (
            error instanceof ClientError &&
            error.statusCode === 422 &&
            isPydanticValidationError(error.detail)
          ) {
            const { fieldErrors, nonFieldErrors } = parsePydanticErrors(error.detail);
            setNameError(fieldErrors['name'] ?? '');
            setEmailError(fieldErrors['email'] ?? '');
            setPasswordError(fieldErrors['password'] ?? '');
            if (nonFieldErrors.length > 0) {
              showError(error, nonFieldErrors[0]);
            } else if (Object.keys(fieldErrors).length === 0) {
              showError(error, 'Sign up failed. Please check your input and try again.');
            }
          } else if (error instanceof ServerError && error.statusCode === 503) {
            showError(error, 'Signup temporarily unavailable, please try again in a few minutes.');
          } else {
            showError(error, 'Sign up failed. Please try again, or contact support if the issue persists.');
          }
        },
      },
    );
  }, [name, email, password, confirmPassword, tosAgreedAt, turnstileToken, signUp, navigate]);

  const retryScriptLoading = useCallback(() => {
    setScriptError(false);
    setScriptLoading(true);
    window.location.reload();
  }, []);

  if ((!isMockMode && scriptLoading) || isGoogleLoginPending) {
    return <Loading />;
  }

  if (scriptError) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Failed to load Google authentication services.</Text>
        <Button name="Retry" onPress={retryScriptLoading} containerStyle={styles.retryButton} />
      </View>
    );
  }

  const hasErrors =
    nameError.length > 0 || emailError.length > 0 || passwordError.length > 0 || confirmPasswordError.length > 0;
  const canSubmit = hasContent && !!tosAgreedAt && !!turnstileToken && !isSignupPending && !hasErrors;

  return (
    <PhaseAnimationProvider phaseCount={3} interval={4500} fadeDuration={theme.animation.long}>
      <PublicPageLayout>
        <View style={isLargeScreen ? styles.contentRowLarge : styles.contentRowSmall}>
          <View style={isLargeScreen ? styles.leftColumnLarge : styles.leftColumnSmall}>
            <View style={styles.formWrapper}>
              {view === 'landing' ? (
                <View style={styles.formContainer}>
                  <SplashPanel />
                  <View style={styles.form}>
                    {isMockMode ? (
                      <Button
                        name="Mock Sign Up"
                        variant="primary"
                        onPress={() => handleGoogleSignup({ credential: 'mock-setup' } as CredentialResponse)}
                      />
                    ) : (
                      <GoogleLogin
                        onSuccess={handleGoogleSignup}
                        onError={handleGoogleSignupError}
                        text="continue_with"
                        size="large"
                        shape="circle"
                        theme="outline"
                        logo_alignment="left"
                        width="304px"
                      />
                    )}
                  </View>
                  <OrDivider />
                  <View style={styles.form}>
                    <Button
                      name="Sign up with email"
                      icon="email"
                      onPress={handleShowEmailForm}
                      containerStyle={styles.emailSignupButton}
                      fontStyle={styles.emailSignupFont}
                    />
                  </View>
                  <Text style={styles.loginPrompt}>
                    {'Have an account? '}
                    <HyperLink onPress={() => navigate('/login')} variant="primary" style={styles.loginPromptLink}>
                      Log in
                    </HyperLink>
                  </Text>
                </View>
              ) : (
                <View style={styles.formContainer}>
                  <View style={styles.form}>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (canSubmit) handleEmailPasswordSignup();
                      }}
                      style={{ display: 'contents' }}>
                      <View style={styles.inputGroup}>
                        <FormInput
                          placeholderText="Name *"
                          value={name}
                          onChangeText={(text) => {
                            setName(text);
                            setNameError('');
                          }}
                          onSubmit={() => canSubmit && handleEmailPasswordSignup()}
                          hasError={nameError.length > 0}
                          errorMessage={nameError}
                          autoComplete="name"
                          autoFocus
                          id="signup-name"
                          containerStyle={styles.inputContainer}
                          inputStyle={styles.roundedInput}
                          formContainerStyle={styles.cardFormContainer}
                        />
                        <FormInput
                          placeholderText="Email *"
                          value={email}
                          onChangeText={(text) => {
                            setEmail(text);
                            setEmailError('');
                          }}
                          onSubmit={() => canSubmit && handleEmailPasswordSignup()}
                          hasError={emailError.length > 0}
                          errorMessage={emailError}
                          autoComplete="email"
                          inputMode="email"
                          id="signup-email"
                          containerStyle={styles.inputContainer}
                          inputStyle={styles.roundedInput}
                          formContainerStyle={styles.cardFormContainer}
                        />
                        <FormInput
                          placeholderText="Password *"
                          value={password}
                          onChangeText={(text) => {
                            setPassword(text);
                            setPasswordError('');
                            setConfirmPasswordError('');
                          }}
                          onSubmit={() => canSubmit && handleEmailPasswordSignup()}
                          hasError={passwordError.length > 0}
                          errorMessage={passwordError}
                          autoComplete="new-password"
                          id="signup-password"
                          shadow
                          containerStyle={styles.inputContainer}
                          inputStyle={styles.roundedInput}
                          formContainerStyle={styles.cardFormContainer}
                        />
                        <FormInput
                          placeholderText="Confirm password *"
                          value={confirmPassword}
                          onChangeText={(text) => {
                            setConfirmPassword(text);
                            setConfirmPasswordError('');
                          }}
                          onSubmit={() => canSubmit && handleEmailPasswordSignup()}
                          hasError={confirmPasswordError.length > 0}
                          errorMessage={confirmPasswordError}
                          autoComplete="new-password"
                          id="signup-confirm-password"
                          shadow
                          containerStyle={styles.inputContainer}
                          inputStyle={styles.roundedInput}
                          formContainerStyle={styles.cardFormContainer}
                        />
                      </View>
                      <Checkbox
                        checkboxContainerStyle={styles.checkboxContainer}
                        isChecked={!!tosAgreedAt}
                        label={tosCheckboxLabel}
                        onToggle={handleTosToggle}
                        telemetryLabel="signup-tos-checkbox"
                      />
                      {turnstileSiteKey && (
                        <Turnstile
                          key={turnstileKey}
                          sitekey={turnstileSiteKey}
                          onVerify={setTurnstileToken}
                          onExpire={() => setTurnstileToken(null)}
                        />
                      )}
                      <Button
                        name="Sign up"
                        variant="primary"
                        disabled={!canSubmit}
                        loading={isSignupPending}
                        onPress={handleEmailPasswordSignup}
                        containerStyle={styles.submitButton}
                      />
                    </form>
                  </View>
                  <Text style={styles.loginPrompt}>
                    <HyperLink onPress={handleGoBack} variant="primary" style={styles.loginPromptLink}>
                      Go back
                    </HyperLink>
                  </Text>
                </View>
              )}
            </View>
          </View>
          {isLargeScreen && <AnimatedGraphicPanel />}
        </View>
      </PublicPageLayout>
    </PhaseAnimationProvider>
  );
}

const styles = StyleSheet.create({
  contentRowSmall: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  contentRowLarge: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    paddingHorizontal: theme.spacing.xxlarge * 2,
    gap: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftColumnSmall: {
    flex: 1,
    paddingHorizontal: theme.spacing.xlarge,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftColumnLarge: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  formWrapper: {
    width: 304,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.xlarge,
    alignSelf: 'stretch',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxlarge,
    alignSelf: 'stretch',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    gap: theme.spacing.medium,
  },
  inputContainer: {
    alignSelf: 'stretch',
  },
  cardFormContainer: {
    height: 48,
    width: '100%',
  },
  roundedInput: {
    borderRadius: theme.borderRadius.large,
    height: 48,
  },
  checkboxContainer: {
    gap: theme.spacing.medium,
  },
  checkboxLabelContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xsmall,
    width: '100%',
  },
  checkboxLabelText: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.regular,
  },
  emailSignupButton: {
    alignSelf: 'stretch',
  },
  emailSignupFont: {
    fontSize: theme.fontSizes.small,
  },
  submitButton: {
    alignSelf: 'stretch',
  },
  loginPrompt: {
    fontSize: theme.fontSizes.xsmall,
    color: theme.colors.darkGray,
  },
  loginPromptLink: {
    fontSize: theme.fontSizes.xsmall,
    textDecorationLine: 'underline',
  },
  rightColumn: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  rightContent: {
    alignItems: 'center',
  },
  graphicImage: {
    height: '65vh',
    maxHeight: 700,
    aspectRatio: 960 / 1440,
  },
  errorText: {
    color: theme.colors.red,
    fontFamily: theme.fonts.regular,
    fontSize: theme.fontSizes.small,
    textAlign: 'center',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xlarge,
    height: '100%',
    padding: theme.spacing.xlarge,
  },
  retryButton: {
    marginTop: theme.spacing.medium,
  },
});
